<template>
    <f7-page
        ref="self"
        no-navbar
        class="withdrawal-create-page"
        @page:beforein="PAGE_BEFORE_IN"
    >
        <div class="title-wrapper">
            <p class="title">{{ $t('withdrawal-create.title') }}</p>
        </div>
        <div class="withdrawal-create-page__content-wrapper">
            <div class="withdrawal-create-page__warning">
                <TransferServiceBlockWarning v-if="transfers.length" @click="handleClickTransferWarning"/>
                <template v-if="viewWithdrawalController.warningBlocks.value.length">
                    <FirebaseBlockWarning
                        v-for="(block, index) in viewWithdrawalController.warningBlocks.value"
                        :key="index"
                        :loading="!account"
                        :message="block"
                    />
                </template>
            </div>

            <WithdrawalInputRequisite @change="changePSystem"/>
            <WithdrawalInputAmount/>
        </div>

        <f7-block class="actions">
            <f7-button
                class="theme-type"
                preloader
                :loading="isSubmitLoading"
                :disabled="isButtonDisabled"
                @click="handleSubmitWithdrawal"
            >
                {{ $t('withdrawal-create.btn-next') }}
            </f7-button>
        </f7-block>
    </f7-page>
</template>

<script lang="ts" setup>
import {f7} from 'framework7-vue';
import {getDevice} from "framework7/shared/get-device";
import {computed, onUnmounted, ref, watch} from 'vue';
import WithdrawalPopupController from '../WithdrawalPopupController';
import {PageOpenIn} from "@enums/PageOpenIn";
import Requisite from "@models/requisite/Requisite";
import CreateWalletWithdrawalDto from "@/services/operations/withdrawal/dto/CreateWalletWithdrawalDto";
import ErrorsService from "@/services/errors-service/ErrorsService";
import RequisitesPageController from "@/views/requisites-group/requisites/RequisitesPageController";
import FirebaseBlockWarning from "@components/atomic/block-warning/FirebaseBlockWarning.vue";
import WithdrawalInputAmount from "./comps/WithdrawalInputAmount.vue";
import WithdrawalInputRequisite from "./comps/WithdrawalInputRequisite.vue";
import TransferServiceBlockWarning from "@components/atomic/block-warning/TransferServiceBlockWarning.vue";
import {ServiceTransferBlockWarning} from "@/services/v2/service-transfer-block-warning/ServiceTransferBlockWarning";
import {FirebaseService} from "@/services/firebase/FirebaseService";
import {FirebaseEvents} from "@/services/firebase/analytics/FirebaseEvents";
import FirebaseEventBuilder from "@/services/firebase/analytics/FirebaseEventBuilder";
import IFrameService from "@/services/iframe/IFrameService";
import {useTranslate} from "@tolgee/vue";

const viewWithdrawalController = WithdrawalPopupController.getInstance();
const {
    amount,
    account,
    activeRequisite,
    isP2PRequisite,
    activeRequisiteMinMaxLoading,
    textMaxMinError,
    minMaxRangesAmountByRequisite,
    inputAmountFocused,
    textRequisiteError,
    routerService,
    isSubmitLoading,
    isMaxLessMinRates,
    isAmountGreaterThanAgentsMax,
    isAmountGreaterThanBalance,
    isAgentsRangeError,
    isBalanceLessMinWithdrawal,
    transfers,
} = viewWithdrawalController;

const {t} = useTranslate();
const self = ref(null);

const isButtonDisabled = computed(() => {
	return isSubmitLoading.value ||
      isMaxLessMinRates.value ||
      isAmountGreaterThanBalance.value ||
      isAgentsRangeError.value ||
      isBalanceLessMinWithdrawal.value ||
      textRequisiteError.value;
})

const handleSubmitWithdrawal = async () => {
    closeKeyboardFocusInput(async () => {
        try {
            isSubmitLoading.value = true;

            if (validate()) {
                if (isP2PRequisite.value) {
                    await viewWithdrawalController.create2P2Withdrawal();
                    //@ts-ignore
                    f7.views.higher.router.back("/higher");
                    f7.view.main.router.navigate('/withdrawal');
                } else {
                    openFullSizePopup();
                    await routerService.toWithdrawalWalletCreatedPage(
                        new CreateWalletWithdrawalDto(
                            amount.value,
                            activeRequisite.value!,
                            activeRequisite.value!.psystem
                        ),
                        PageOpenIn.POPUP
                    );
                }
            }
        } catch (e) {
	        closePopup()
            ErrorsService.of().handle(e);
        } finally {
            isSubmitLoading.value = false;
        }
    });
}

const handleClickTransferWarning = () => {
    closeKeyboardFocusInput(async () => {
        ServiceTransferBlockWarning.of().triggered = true;
        //@ts-ignore
        f7.views.higher.router.back("/higher");
        FirebaseService.of().analyticsEvent(
            FirebaseEvents.WITHDRAWAL_TO_SERVICES_CLICK,
            FirebaseEventBuilder.of().build()
        );
    });
}

const validate = () => {
    return validateRequisite() && validateMinMax();
}

const validateRequisite = () => {
    if (activeRequisite.value == null) {
        textRequisiteError.value = true;
        return false;
    }

    return true;
}
const validateMinMax = () => {
    const {min, max} = minMaxRangesAmountByRequisite.value;
    const result = !(amount.value < min || amount.value > max || amount.value > account.value.balance.amount);

    textMaxMinError.value = !result;

    return result;
}
const fetchRequisiteMinMax = async (requisite: Requisite) => {
    try {
        activeRequisiteMinMaxLoading.value = true;
        await viewWithdrawalController.fetchRequisiteMinMax(requisite);
        viewWithdrawalController.checkRequisiteMinMaxAndSetInput();
    } catch (e: any) {
        ErrorsService.of().handle(e);
    } finally {
        activeRequisiteMinMaxLoading.value = false;
    }
}
const closePopup = () => {
    const popupEl: HTMLDivElement | null =  document.querySelector('.withdrawal-popup');
    if (popupEl) {
	    f7.popup.get(popupEl).close();
    }
}
const PAGE_BEFORE_IN = () => {
    const popupHTML = document.querySelector('.withdrawal-popup');
    (popupHTML as HTMLDivElement).classList.remove('height-100');

    setTimeout(() => {
        setHeightPopup();
    }, 0)
}

const setHeightPopup = () => {
    if (IFrameService.of().isIFrameView) return;

    const mainEl = self.value as any;
    if (!mainEl) return;
    let height = 0;
    const style = getComputedStyle(document.body);
    const children = mainEl.$el.querySelector('.page-content').children;

    for (let i = 0; i < children.length; i++) {
        const el = (children[i] as HTMLElement);
        const styleEl = getComputedStyle(el);
        const rect = el.getBoundingClientRect()
        height += rect.height + parseFloat(styleEl['marginTop']) + parseFloat(styleEl['marginBottom']);
    }

    mainEl.$el.closest('.withdrawal-popup').style.height = Math.ceil(height + parseInt(style.getPropertyValue('--f7-safe-area-bottom'))) + 'px';
    setTimeout(() => {
        setHeightPopup();
    }, 0)
}

const closeKeyboardFocusInput = (callback: Function) => {
    if (getDevice().cordova) {
        inputAmountFocused.value = false;
        setTimeout(async () => {
            await callback();
        }, 600);
        return false;
    }
    callback();
}

const openFullSizePopup = () => {
    const popupHTML = document.querySelector('.withdrawal-popup');
    (popupHTML as HTMLDivElement).classList.add('height-100');
}

const changePSystem = async () => {
    closeKeyboardFocusInput(async () => {
        textRequisiteError.value = false;
        textMaxMinError.value = false;

        const popupHTML = document.querySelector('.withdrawal-popup');
        f7.preloader.show();

        if (activeRequisite.value || RequisitesPageController.of().requisites.value.length > 0) {
            await routerService.toRequisitesPage(PageOpenIn.POPUP, function () {
                (popupHTML as HTMLDivElement).classList.add('height-100');
                f7.preloader.hide();
            });

            return;
        }

        try {
            f7.preloader.show();
            await viewWithdrawalController.fetchAddressTypes();
            viewWithdrawalController.initRequisitePayload();

            await routerService.toRequisitesPage(PageOpenIn.POPUP, function () {
                (popupHTML as HTMLDivElement).classList.add('height-100');
            });
        } catch (e: any) {
            f7.dialog.alert(t.value('g.errors.alert.default-text'), t.value('g.errors.alert.default-title'));
        } finally {
            f7.preloader.hide();
        }
    });
}

watch(() => activeRequisite.value, async (newValue: Requisite | null) => {
    if (newValue !== null) {
        await fetchRequisiteMinMax(newValue);
    }
});

onUnmounted(() => {
    if (activeRequisite.value) {
        activeRequisite.value = null;
    }
});
</script>

<style lang="scss">
.withdrawal-create-page {
    position: relative !important;
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;
    --f7-navbar-height: 0px;

    &__warning {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin: 20px 16px 24px 16px;
    }

    .page-content {
        --f7-page-navbar-offset: 0;

        &::-webkit-scrollbar {
            display: none;
        }
    }

    .title-wrapper {
        margin: 24px 16px;

        .title {
            font-weight: 700;
            font-size: 24px;
            line-height: 26px;
            letter-spacing: 0.337647px;
            color: #000000;
        }
    }

    .actions {
        margin-top: 24px;
        margin-bottom: 16px;

        .button {

            height: 46px;
        }
    }
}
</style>
